import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "./../../firebase.js";
import "./Voting.css";
import { getStoredAccessMode, isPremiumAccess } from "../../utils/accessMode";
import AppImage from "../common/AppImage";
import { isCloudinaryConfigured, uploadImageToCloudinary } from "../../utils/cloudinary";
import {
  FINAL_AWARD_ID,
  getAwardDescription,
  getAwardMaxNominees,
  getAwardName,
  getNomineeImage,
  getNomineeName,
  getSpecialAwards,
  isPremiumNominationAward,
} from "../../utils/awards";
import { getPointsForPosition } from "../../utils/voting";

const FINAL_POSITIONS = [
  { key: "primero", label: "Primer puesto", rank: "1" },
  { key: "segundo", label: "Segundo puesto", rank: "2" },
  { key: "tercero", label: "Tercer puesto", rank: "3" },
];

const createEmptyPodium = () => ({
  primero: "",
  segundo: "",
  tercero: "",
});

const createEmptyPremiumNomineeForm = () => ({
  name: "",
  imageUrl: "",
});

const normalizeNomineeLabel = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getStoredSpecialVotes = (userData, awardId) => {
  if (awardId === FINAL_AWARD_ID && userData.finalVote) {
    return userData.finalVote;
  }

  return userData.specialVotes?.[awardId] || null;
};

const mapStoredVotesToSelections = (storedVotes, isPremium) => {
  if (!storedVotes) {
    return createEmptyPodium();
  }

  const selections = createEmptyPodium();

  FINAL_POSITIONS.forEach((position) => {
    const expectedPoints = getPointsForPosition(position.rank, 3, isPremium);
    const matchedNominee = Object.entries(storedVotes).find(([, points]) => points === expectedPoints);

    if (matchedNominee) {
      selections[position.key] = matchedNominee[0];
    }
  });

  return selections;
};

const FinalVote = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const accessMode = location.state?.accessMode || getStoredAccessMode();
  const isPremium = isPremiumAccess(accessMode) || Boolean(location.state?.isPremium);
  const user = auth.currentUser;
  const [specialAwards, setSpecialAwards] = useState([]);
  const [nomineesByAward, setNomineesByAward] = useState({});
  const [selectedVotesByAward, setSelectedVotesByAward] = useState({});
  const [savedAwards, setSavedAwards] = useState({});
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [premiumNomineeForm, setPremiumNomineeForm] = useState(createEmptyPremiumNomineeForm());
  const [isUploadingPremiumNomineeImage, setIsUploadingPremiumNomineeImage] = useState(false);
  const [isSavingPremiumNominee, setIsSavingPremiumNominee] = useState(false);
  const premiumNomineeImageInputRef = useRef(null);

  const currentAward = specialAwards[currentIndex] || null;
  const currentAwardId = currentAward?.id || "";
  const currentAwardName = getAwardName(currentAward);
  const currentSelections = selectedVotesByAward[currentAwardId] || createEmptyPodium();
  const currentNominees = useMemo(
    () => nomineesByAward[currentAwardId] || [],
    [currentAwardId, nomineesByAward]
  );
  const canSuggestPremiumNominee = isPremium && isPremiumNominationAward(currentAward);
  const premiumNomineeLimitReached =
    canSuggestPremiumNominee &&
    currentNominees.length >= getAwardMaxNominees(currentAward, currentAwardId);

  const displayNominados = useMemo(
    () =>
      FINAL_POSITIONS.reduce((accumulator, position) => {
        accumulator[position.key] = currentNominees.find(
          (nominado) => getNomineeName(nominado) === currentSelections[position.key]
        );
        return accumulator;
      }, {}),
    [currentNominees, currentSelections]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchSpecialAwards = async () => {
      try {
        const premiosSnapshot = await getDocs(collection(db, "premios"));
        const allAwards = premiosSnapshot.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }));
        const specialAwardsList = getSpecialAwards(allAwards);
        const nomineesEntries = await Promise.all(
          specialAwardsList.map(async (award) => {
            const querySnapshot = await getDocs(collection(db, "premios", award.id, "nominados"));

            return [
              award.id,
              querySnapshot.docs.map((snapshot) => ({
                id: snapshot.id,
                ...snapshot.data(),
              })),
            ];
          })
        );
        const nomineesMap = Object.fromEntries(nomineesEntries);
        const initialSelections = {};
        const initialSavedState = {};

        if (user) {
          const userSnapshot = await getDoc(doc(db, "users", user.uid));
          const userData = userSnapshot.data() || {};

          specialAwardsList.forEach((award) => {
            const storedVotes = getStoredSpecialVotes(userData, award.id);
            initialSelections[award.id] = mapStoredVotesToSelections(storedVotes, isPremium);
            initialSavedState[award.id] = Boolean(storedVotes);
          });
        } else {
          specialAwardsList.forEach((award) => {
            initialSelections[award.id] = createEmptyPodium();
            initialSavedState[award.id] = false;
          });
        }

        if (!cancelled) {
          setSpecialAwards(specialAwardsList);
          setNomineesByAward(nomineesMap);
          setSelectedVotesByAward(initialSelections);
          setSavedAwards(initialSavedState);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error al cargar los premios especiales:", error);
          setFeedback({
            type: "error",
            text: "No hemos podido preparar los premios especiales.",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSpecialAwards();

    return () => {
      cancelled = true;
    };
  }, [isPremium, user]);

  useEffect(() => {
    setPremiumNomineeForm(createEmptyPremiumNomineeForm());
  }, [currentAwardId]);

  const handleSelectVote = (position, value) => {
    const alreadyUsedByAnotherSlot = Object.entries(currentSelections).some(
      ([key, currentValue]) => key !== position && currentValue === value && value
    );

    if (alreadyUsedByAnotherSlot) {
      setFeedback({
        type: "error",
        text: "Cada finalista solo puede ocupar una posicion del podio.",
      });
      return;
    }

    setSelectedVotesByAward((previousSelections) => ({
      ...previousSelections,
      [currentAwardId]: {
        ...currentSelections,
        [position]: value,
      },
    }));
    setSavedAwards((previousState) => ({
      ...previousState,
      [currentAwardId]: false,
    }));
    setFeedback({ type: "", text: "" });
  };

  const handlePremiumNomineeFieldChange = (field, value) => {
    setPremiumNomineeForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const handleUploadPremiumNomineeImage = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!canSuggestPremiumNominee) {
      event.target.value = "";
      return;
    }

    if (!file.type.startsWith("image/")) {
      setFeedback({
        type: "error",
        text: "Selecciona una imagen valida para el nuevo nominado.",
      });
      event.target.value = "";
      return;
    }

    setIsUploadingPremiumNomineeImage(true);
    setFeedback({ type: "", text: "" });

    try {
      const uploadResult = await uploadImageToCloudinary(file);

      setPremiumNomineeForm((currentForm) => ({
        ...currentForm,
        imageUrl: uploadResult.secureUrl,
      }));
      setFeedback({
        type: "success",
        text: "La imagen principal se ha subido correctamente y ya esta lista para guardar.",
      });
    } catch (error) {
      console.error("Error al subir la imagen principal del nuevo nominado:", error);
      setFeedback({
        type: "error",
        text: "No hemos podido subir la imagen principal a Cloudinary.",
      });
    } finally {
      setIsUploadingPremiumNomineeImage(false);
      event.target.value = "";
    }
  };

  const handleSavePremiumNominee = async () => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    if (!canSuggestPremiumNominee || !currentAward) {
      return;
    }

    const trimmedName = premiumNomineeForm.name.trim();

    if (!trimmedName) {
      setFeedback({
        type: "error",
        text: "Introduce el nombre del nuevo nominado antes de guardarlo.",
      });
      return;
    }

    if (!premiumNomineeForm.imageUrl.trim()) {
      setFeedback({
        type: "error",
        text: "Sube la imagen principal antes de guardar el nuevo nominado.",
      });
      return;
    }

    if (premiumNomineeLimitReached) {
      setFeedback({
        type: "error",
        text: "Este premio ya ha alcanzado el limite actual de nominados.",
      });
      return;
    }

    const nomineeAlreadyExists = currentNominees.some(
      (nominee) => normalizeNomineeLabel(getNomineeName(nominee)) === normalizeNomineeLabel(trimmedName)
    );

    if (nomineeAlreadyExists) {
      setFeedback({
        type: "error",
        text: "Ya existe un nominado con ese nombre en Tofu promesa.",
      });
      return;
    }

    setIsSavingPremiumNominee(true);
    setFeedback({ type: "", text: "" });

    try {
      const nomineePayload = {
        nombre: trimmedName,
        name: trimmedName,
        imageURL: premiumNomineeForm.imageUrl.trim(),
        imageUrl: premiumNomineeForm.imageUrl.trim(),
        votes: 0,
        votedUsers: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const newNomineeRef = await addDoc(collection(db, "premios", currentAward.id, "nominados"), nomineePayload);
      const savedNominee = {
        id: newNomineeRef.id,
        ...nomineePayload,
      };

      setNomineesByAward((previousNominees) => ({
        ...previousNominees,
        [currentAward.id]: [...(previousNominees[currentAward.id] || []), savedNominee].sort((left, right) =>
          getNomineeName(left).localeCompare(getNomineeName(right), "es", { sensitivity: "base" })
        ),
      }));
      setPremiumNomineeForm(createEmptyPremiumNomineeForm());
      setFeedback({
        type: "success",
        text: `${trimmedName} se ha anadido correctamente a ${currentAwardName}.`,
      });
    } catch (error) {
      console.error("Error al guardar el nuevo nominado premium:", error);
      setFeedback({
        type: "error",
        text: "No hemos podido guardar este nominado en Firebase.",
      });
    } finally {
      setIsSavingPremiumNominee(false);
    }
  };

  const handleSubmitVotes = async () => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    if (!currentAward) {
      return;
    }

    if (Object.values(currentSelections).some((value) => !value)) {
      setFeedback({
        type: "error",
        text: `Completa el podio entero de ${currentAwardName} antes de guardarlo.`,
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: "", text: "" });

    try {
      const awardVotePayload = {};

      FINAL_POSITIONS.forEach((position) => {
        const nomineeName = currentSelections[position.key];

        if (!nomineeName) {
          return;
        }

        awardVotePayload[nomineeName] = getPointsForPosition(position.rank, 3, isPremium);
      });

      for (const nominee of currentNominees) {
        const nomineeName = getNomineeName(nominee);
        const points = awardVotePayload[nomineeName] || 0;
        const nomineeRef = doc(db, "premios", currentAward.id, "nominados", nominee.id);
        const nomineeSnapshot = await getDoc(nomineeRef);
        const nomineeData = nomineeSnapshot.data() || {};
        const updatedVotedUsers = {
          ...(nomineeData.votedUsers || {}),
        };

        if (points > 0) {
          updatedVotedUsers[user.uid] = {
            email: user.email || "",
            timestamp: new Date().toISOString(),
            vote: points,
          };
        } else {
          delete updatedVotedUsers[user.uid];
        }

        const totalPoints = Object.values(updatedVotedUsers).reduce(
          (sum, userVote) => sum + (userVote.vote || 0),
          0
        );

        await updateDoc(nomineeRef, {
          votedUsers: updatedVotedUsers,
          votes: totalPoints,
        });
      }

      const userPayload = {
        specialVotes: {
          [currentAward.id]: awardVotePayload,
        },
      };

      if (currentAward.id === FINAL_AWARD_ID) {
        userPayload.finalVote = awardVotePayload;
      }

      await setDoc(doc(db, "users", user.uid), userPayload, { merge: true });

      setSavedAwards((previousState) => ({
        ...previousState,
        [currentAward.id]: true,
      }));
      setFeedback({
        type: "success",
        text: `${currentAwardName} se ha guardado correctamente.`,
      });
    } catch (error) {
      console.error("Error al registrar el premio especial:", error);
      setFeedback({
        type: "error",
        text: `No hemos podido guardar ${currentAwardName}. Intentalo otra vez.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="voting-shell">
        <div className="voting-loader-card">
          <p className="section-kicker">Premios especiales</p>
          <h1>Preparando los grandes reconocimientos</h1>
          <p>Estamos cargando los podios mas exigentes de la gala.</p>
        </div>
      </div>
    );
  }

  if (!specialAwards.length) {
    return (
      <div className="voting-shell">
        <div className="voting-loader-card error">
          <p className="section-kicker">Premios especiales</p>
          <h1>Aun no hay reconocimientos especiales listos</h1>
          <p>
            Crea o configura los premios especiales desde el panel admin para abrir esta seccion.
          </p>
          <button
            className="button-next-back"
            type="button"
            onClick={() => navigate("/home", { state: { accessMode, isPremium } })}
          >
            Volver a la home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="voting-shell">
      <section className="voting-header-card final">
        <div>
          <p className="section-kicker">
            Premio especial {currentIndex + 1} de {specialAwards.length}
          </p>
          <h1 className="VotingTitle">{currentAwardName}</h1>
          <p className="subtitle">{getAwardDescription(currentAward)}</p>
        </div>

        <div className="score-card compact">
          <p className="score-card-label">Estado del reconocimiento</p>
          <strong>{savedAwards[currentAwardId] ? "Premio guardado" : "Pendiente de guardar"}</strong>
          <span>
            {isPremium
              ? "Tu voto premium ya entra con ponderacion reforzada en este podio."
              : "Ordena tu podio y guarda este premio especial cuando lo tengas claro."}
          </span>
        </div>
      </section>

      <div className="progress-bar" aria-label="Navegacion entre premios especiales">
        {specialAwards.map((award, index) => (
          <button
            key={award.id}
            type="button"
            className={`progress-dot ${currentIndex === index ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Ir al premio especial ${index + 1}`}
          >
            {String(index + 1).padStart(2, "0")}
          </button>
        ))}
      </div>

      {feedback.text ? (
        <p className={`feedback-message ${feedback.type}`} aria-live="polite">
          {feedback.text}
        </p>
      ) : null}

      {canSuggestPremiumNominee ? (
        <section className="premium-nominee-card">
          <div className="premium-nominee-copy">
            <p className="section-kicker subtle">Acceso premium</p>
            <h2>Anadir nominado a Tofu promesa</h2>
            <p>
              Este es el unico premio donde un usuario premium puede proponer un nuevo nominado
              desde la web. Solo necesitas el nombre y la imagen principal.
            </p>
          </div>

          <div className="premium-nominee-form">
            <label className="vote-field">
              <span>Nombre del nuevo nominado</span>
              <input
                className="vote-input"
                type="text"
                value={premiumNomineeForm.name}
                onChange={(event) => handlePremiumNomineeFieldChange("name", event.target.value)}
                placeholder="Escribe el nombre"
                maxLength={80}
              />
            </label>

            <div className="premium-upload-block">
              <input
                ref={premiumNomineeImageInputRef}
                className="premium-hidden-input"
                type="file"
                accept="image/*"
                onChange={handleUploadPremiumNomineeImage}
              />
              <button
                className="button-next-back"
                type="button"
                onClick={() => premiumNomineeImageInputRef.current?.click()}
                disabled={isUploadingPremiumNomineeImage || isSavingPremiumNominee || !isCloudinaryConfigured()}
              >
                {isUploadingPremiumNomineeImage ? "Subiendo imagen..." : "Subir imagen principal"}
              </button>
              <span className="premium-upload-hint">
                {isCloudinaryConfigured()
                  ? "La imagen se sube a Cloudinary y queda lista para guardarse en imageUrl."
                  : "Configura Cloudinary para activar esta subida directa."}
              </span>
            </div>

            {premiumNomineeForm.imageUrl ? (
              <div className="premium-preview-card">
                <AppImage
                  src={premiumNomineeForm.imageUrl}
                  alt={premiumNomineeForm.name || "Vista previa del nuevo nominado"}
                  className="premium-preview-image"
                  fallbackClassName="nominado-placeholder compact"
                />
              </div>
            ) : null}

            <div className="premium-submit-row">
              <button
                className="button-voting"
                type="button"
                onClick={handleSavePremiumNominee}
                disabled={
                  isUploadingPremiumNomineeImage ||
                  isSavingPremiumNominee ||
                  premiumNomineeLimitReached ||
                  !isCloudinaryConfigured()
                }
              >
                {isSavingPremiumNominee ? "Guardando nominado..." : "Guardar nominado en Tofu promesa"}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="final-vote-grid">
        {FINAL_POSITIONS.map((position) => {
          const selectedNominee = displayNominados[position.key];
          const previewImage = selectedNominee ? getNomineeImage(selectedNominee) : "";

          return (
            <article key={position.key} className="final-vote-card">
              <div className="final-vote-topline">
                <span>{position.label}</span>
                <strong>Podio</strong>
              </div>

              <label className="vote-field">
                <span>Selecciona un finalista</span>
                <select
                  className={`vote-select ${currentSelections[position.key] ? "" : "is-placeholder"}`.trim()}
                  value={currentSelections[position.key]}
                  onChange={(event) => handleSelectVote(position.key, event.target.value)}
                >
                  <option value="">Selecciona</option>
                  {currentNominees.map((nominado) => {
                    const nomineeName = getNomineeName(nominado);

                    return (
                      <option key={`${currentAwardId}-${position.key}-${nominado.id}`} value={nomineeName}>
                        {nomineeName}
                      </option>
                    );
                  })}
                </select>
              </label>

              <div className="final-preview">
                {selectedNominee && previewImage ? (
                  <>
                    <AppImage
                      src={previewImage}
                      alt={getNomineeName(selectedNominee)}
                      className="final-preview-image"
                      fallbackClassName="nominado-placeholder compact"
                    />
                    <h2>{getNomineeName(selectedNominee)}</h2>
                  </>
                ) : selectedNominee ? (
                  <>
                    <div className="nominado-placeholder compact">Sin imagen externa asignada</div>
                    <h2>{getNomineeName(selectedNominee)}</h2>
                  </>
                ) : (
                  <div className="nominado-placeholder compact">Tu seleccion aparecera aqui</div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      <div className="voting-footer">
        <div className="navigation-buttons">
          <button
            className="button-next-back"
            type="button"
            onClick={() => navigate("/special-awards/intro", { state: { accessMode, isPremium } })}
          >
            Volver a la antesala
          </button>
          <button
            className="button-next-back"
            type="button"
            onClick={() => setCurrentIndex((previousIndex) => Math.max(previousIndex - 1, 0))}
            disabled={currentIndex === 0 || isSubmitting}
          >
            Premio anterior
          </button>
          <button
            className="button-next-back"
            type="button"
            onClick={() =>
              setCurrentIndex((previousIndex) => Math.min(previousIndex + 1, specialAwards.length - 1))
            }
            disabled={currentIndex === specialAwards.length - 1 || isSubmitting}
          >
            Siguiente premio
          </button>
        </div>

        <div className="voting-actions">
          <button className="button-voting" type="button" onClick={handleSubmitVotes} disabled={isSubmitting}>
            {isSubmitting ? "Guardando premio especial..." : "Guardar este premio especial"}
          </button>
          <button
            className="button-final-vote"
            type="button"
            onClick={() => navigate("/goodbye")}
            disabled={isSubmitting || Object.values(savedAwards).some((isSaved) => !isSaved)}
          >
            Cerrar la seccion especial
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalVote;
